import {
  addDoc,
  arrayUnion,
  collection,
  doc,
  getDoc,
  getDocs,
  increment,
  query,
  runTransaction,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from "firebase/firestore";

import { db } from "../firebase/config";

const INVITE_PREFIX = "TUNE";
const INVITE_LENGTH = 6;
const INVITE_CHARACTERS = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";

function generateInviteCode() {
  // Cryptographically random, not Math.random(). The 32-char alphabet divides
  // 256 evenly, so the byte % length mapping is unbiased.
  const bytes = new Uint8Array(INVITE_LENGTH);
  crypto.getRandomValues(bytes);

  let code = "";

  for (let index = 0; index < INVITE_LENGTH; index++) {
    code += INVITE_CHARACTERS[bytes[index] % INVITE_CHARACTERS.length];
  }

  return `${INVITE_PREFIX}-${code}`;
}

function normalizeInviteCode(inviteCode) {
  return inviteCode.trim().toUpperCase().replace(/\s+/g, "");
}

export async function createGroup(name, owner) {
  const groupName = name.trim();

  if (!groupName) {
    throw new Error("GROUP_NAME_REQUIRED");
  }

  const groupRef = await addDoc(collection(db, "groups"), {
    name: groupName,
    ownerId: owner.uid,
    ownerEmail: owner.email,

    memberIds: [owner.uid],
    memberCount: 1,

    inviteCode: "",

    createdAt: serverTimestamp(),
  });

  const inviteCode = generateInviteCode();

  await updateDoc(doc(db, "groups", groupRef.id), {
    inviteCode,
  });

  // Publish the code → groupId mapping so a non-member can resolve this group by
  // its code without being able to read (or enumerate) the groups collection.
  await setDoc(doc(db, "inviteCodes", inviteCode), {
    groupId: groupRef.id,
    ownerId: owner.uid,
    createdAt: serverTimestamp(),
  });

  return {
    id: groupRef.id,
    name: groupName,
    inviteCode,
    memberIds: [owner.uid],
    memberCount: 1,
  };
}

// Lazy migration: groups created before the /inviteCodes lookup existed have no
// mapping doc, so joining them by code would fail. When an owner loads their
// groups we (idempotently) republish the mapping for any group they own. No
// admin credentials needed — the rules let an owner claim a code for their group.
export async function ensureInviteCode(group, user) {
  if (!group?.id || !group?.inviteCode || group.ownerId !== user?.uid) {
    return;
  }

  const codeRef = doc(db, "inviteCodes", group.inviteCode);
  const snapshot = await getDoc(codeRef);

  if (snapshot.exists()) {
    return;
  }

  await setDoc(codeRef, {
    groupId: group.id,
    ownerId: user.uid,
    createdAt: serverTimestamp(),
  });
}

export async function joinGroup(inviteCode, user) {
  const cleanCode = normalizeInviteCode(inviteCode);

  if (!cleanCode) {
    throw new Error("INVITE_CODE_REQUIRED");
  }

  // Resolve the code → groupId via the /inviteCodes lookup. Groups are no longer
  // readable by non-members, so we cannot query the groups collection by code.
  const codeSnapshot = await getDoc(doc(db, "inviteCodes", cleanCode));

  if (!codeSnapshot.exists()) {
    throw new Error("GROUP_NOT_FOUND");
  }

  const { groupId } = codeSnapshot.data();
  const groupRef = doc(db, "groups", groupId);

  // Blind membership write: a non-member cannot read the group first, so we
  // append ourselves with arrayUnion/increment and let the rules enforce
  // "add exactly yourself" (isJoiningSelf) and the 5-member cap.
  try {
    await updateDoc(groupRef, {
      memberIds: arrayUnion(user.uid),
      memberCount: increment(1),
    });
  } catch (error) {
    // The write was denied. Either we are already a member (the increment then
    // breaks memberCountMatches) or the group is full. We can tell the two apart
    // by whether we're allowed to READ the group: only members can.
    try {
      const existing = await getDoc(groupRef);

      if (existing.exists() && (existing.data().memberIds || []).includes(user.uid)) {
        const data = existing.data();

        return {
          id: groupId,
          name: data.name,
          inviteCode: data.inviteCode,
          memberIds: data.memberIds,
          memberCount: data.memberCount,
        };
      }
    } catch (readError) {
      // Not a member and can't read ⇒ genuinely full / unavailable.
    }

    throw new Error("GROUP_FULL");
  }

  // We are now a member, so reading the group is allowed.
  const groupSnapshot = await getDoc(groupRef);
  const data = groupSnapshot.data();

  return {
    id: groupId,
    name: data.name,
    inviteCode: data.inviteCode,
    memberIds: data.memberIds,
    memberCount: data.memberCount,
  };
}

export async function renameGroup(groupId, name) {
  const newName = name.trim();

  if (!newName) {
    throw new Error("GROUP_NAME_REQUIRED");
  }

  await updateDoc(doc(db, "groups", groupId), {
    name: newName,
  });

  return {
    id: groupId,
    name: newName,
  };
}

export async function leaveGroup(groupId, user) {
  const groupRef = doc(db, "groups", groupId);

  return runTransaction(db, async (transaction) => {
    const groupSnapshot = await transaction.get(groupRef);

    if (!groupSnapshot.exists()) {
      throw new Error("GROUP_NOT_FOUND");
    }

    const groupData = groupSnapshot.data();
    const memberIds = groupData.memberIds || [];
    const isMember = memberIds.includes(user.uid);

    if (!isMember) {
      throw new Error("NOT_A_MEMBER");
    }

    if (groupData.ownerId === user.uid && memberIds.length <= 1) {
      throw new Error("OWNER_CANNOT_LEAVE");
    }

    const updatedMemberIds = memberIds.filter((id) => id !== user.uid);
    const updatedCount = updatedMemberIds.length;

    transaction.update(groupRef, {
      memberIds: updatedMemberIds,
      memberCount: updatedCount,
    });

    return {
      id: groupSnapshot.id,
      ...groupData,
      memberIds: updatedMemberIds,
      memberCount: updatedCount,
    };
  });
}

export async function getUserGroups(userId) {
  const groupQuery = query(
    collection(db, "groups"),
    where("memberIds", "array-contains", userId),
  );

  const snapshot = await getDocs(groupQuery);

  return snapshot.docs.map((document) => ({
    id: document.id,
    ...document.data(),
  }));
}
